import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { ConfigHelper } from './helpers/config.helper';
import {
  InstitutionDto,
  PersonalLookupInfoForTestRegistrationDto,
  RegInShoppingCartRequest,
  ResultWrapper,
  SaveUserRegIdentityInfoRequest,
  ScoreLevelNonSensitiveDto,
  ScoreRecipientInstitutionDto,
  TestPurposeDto,
  TestScoresOptionDto,
  TestSessionForRegistrationDto,
  UserAccountRegDto,
  ValidateTestSessionSelectionResultDto,
  TestSessionsForSittingSelectionDto,
  TestCentreDto,
  UserAccountIdentityInfoDto,
  AddChildAccountRegToShoppingCartRequestForCael,
  AddChildAccountRegToShoppingCartRequestForCelpip,
  AddChildAccountRegToShoppingCartResponse,
  CreateAccountInitializationInfoDto,
  ShoppingCartDto,
  BiometricConsentRequest,
  UserAccountNotificationSettingsDto,
  ScoreFlagDto,
  OneSkillRetakeRegistrationRequest
} from '../dtos/dto';
import { Store } from '@ngrx/store';
import { setTestPurposeLookup } from 'src/app/state/reg-data.actions';
import { TestPurposeEnum, TestTypeEnum } from '../dtos/enum';
import { HotToastService } from '@ngneat/hot-toast';

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  constructor(
    private http: HttpClient,
    private configHelper: ConfigHelper,
    private toastService: HotToastService,
    private store: Store
  ) { }

  getRegPersonalLookupInfo(countryId: number, testPurposeId: number, testTypeId: number, testCentreId: number): Observable<ResultWrapper<PersonalLookupInfoForTestRegistrationDto>> {
    const params = new HttpParams()
      .set('countryId', countryId.toString())
      .set('rnd', new Date().getTime().toString())
      .set('testPurposeId', testPurposeId.toString())
      .set('testTypeId', testTypeId.toString())
      .set('testCentreId', testCentreId.toString());

    return this.http.get<ResultWrapper<PersonalLookupInfoForTestRegistrationDto>>(this.configHelper.getApiUrl("User/Reg/PersonalLookupInfo"), { params });
  }

  getTestSessionInfo(siteCode: string, testTypeId: number): Observable<ResultWrapper<TestSessionForRegistrationDto>> {
    const params = new HttpParams()
      .set('siteCode', siteCode.toString())
      .set('testTypeId', testTypeId.toString());

    return this.http.get<ResultWrapper<TestSessionForRegistrationDto>>(this.configHelper.getApiUrl("TestSession"), { params });
  }

  getTestPurposes(testTypeId: number): Observable<ResultWrapper<TestPurposeDto[]>> {
    const params = new HttpParams()
      .set('testTypeId', testTypeId.toString());

    return this.http.get<ResultWrapper<TestPurposeDto[]>>(this.configHelper.getApiUrl("TestPurposes"), { params });
  }

  getAdmissionInstitutions(testTypeId: number, isWebDelivery: boolean): Observable<ResultWrapper<InstitutionDto[]>> {
    const params = new HttpParams()
      .set('testTypeId', testTypeId.toString())
      .set('isWebDelivery', isWebDelivery.toString());
    return this.http.get<ResultWrapper<InstitutionDto[]>>(this.configHelper.getApiUrl("AdmissionInstitutions"), { params });
  }

  getSubscribedInstitutions(testPurposeId: number): Observable<ResultWrapper<InstitutionDto[]>> {
    const params = new HttpParams()
      .set('testPurposeId', testPurposeId.toString());
    return this.http.get<ResultWrapper<InstitutionDto[]>>(this.configHelper.getApiUrl("SubscribedInstitutions"), { params });
  }

  getSubscribedInstitutionsList(testTypeId: number, isWebDelivery: boolean): Observable<ResultWrapper<Map<TestPurposeEnum, InstitutionDto[]>>> {
    const params = new HttpParams()
      .set('testTypeId', testTypeId.toString())
      .set('isWebDelivery', isWebDelivery.toString());
    return this.http.get<ResultWrapper<Map<TestPurposeEnum, InstitutionDto[]>>>(this.configHelper.getApiUrl("SubscribedInstitutionsList"), { params });
  }

  uploadDocument(regId: number, document: FormData, regBlobDataTypeId: number) {
    const params = new HttpParams()
      .set('regId', regId.toString()).set('regBlobDataTypeId', regBlobDataTypeId.toString());
    return this.http.post(this.configHelper.getApiUrl("User/Reg/UploadScannedDocument"), document, { params });
  }

  uploadPhoto(regId: number, photo: FormData) {
    const params = new HttpParams()
      .set('regId', regId.toString());
    return this.http.post(this.configHelper.getApiUrl("User/Reg/UploadPhoto"), photo, { params });
  }

  getActiveScoreRecipients(countryId: number, testTypeId: number, regId: number, isWebDelivery: boolean): Observable<ResultWrapper<ScoreRecipientInstitutionDto[]>> {
    return this.http.get<ResultWrapper<ScoreRecipientInstitutionDto[]>>(this.configHelper.getApiUrl("ScoreRecipients/") +
      countryId.toString() + "/" + testTypeId.toString() + "/" + regId.toString() + "/" + isWebDelivery.toString(), {});
  }

  checkBulkDownloadFeatureForCurrentInstitution(institutionId: number): Observable<ResultWrapper<boolean>> {
    return this.http.get<ResultWrapper<boolean>>(this.configHelper.getApiUrl("ScoreRecipients/") + institutionId.toString(), {});
  }

  getInstitutions_notUsed_toDelete(testTypeId: number): Observable<ResultWrapper<InstitutionDto[]>> {
    const params = new HttpParams()
      .set('testTypeId', testTypeId.toString());
    return this.http.get<ResultWrapper<InstitutionDto[]>>(this.configHelper.getApiUrl("AdmissionInstitutions"), { params });
  }

  getScoreLevelsNonSensitive(testTypeId: number): Observable<ResultWrapper<ScoreLevelNonSensitiveDto[]>> {
    const params = new HttpParams()
      .set('testTypeId', testTypeId.toString());
    return this.http.get<ResultWrapper<ScoreLevelNonSensitiveDto[]>>(this.configHelper.getApiUrl("ScoreLevelsNonSensitive"), { params });
  }

  getTestScoresOptions(regId: number): Observable<ResultWrapper<TestScoresOptionDto>> {
    return this.http.get<ResultWrapper<TestScoresOptionDto>>(this.configHelper.getApiUrl("User/Reg/TestScoresOptions/") + regId.toString(), {});
  }

  getTestScoresOptionsForExpiredTestSessionReg(regId: number): Observable<ResultWrapper<TestScoresOptionDto>> {
    return this.http.get<ResultWrapper<TestScoresOptionDto>>(this.configHelper.getApiUrl("/User/Reg/TestScoresOptionsForExpiredTestSessionReg/") + regId.toString(), {});
  }

  setTestScoresOptions(testScoreOptionRequest: TestScoresOptionDto): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Reg/TestScoresOptions"), testScoreOptionRequest);
  }

  setTestScoresOptionsForExpiredTestSessionReg(testScoreOptionRequest: TestScoresOptionDto): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Reg/TestScoresOptionsForExpiredTestSessionReg"), testScoreOptionRequest);
  }

  getTestInfo(regId: number): Observable<ResultWrapper<UserAccountRegDto>> {
    const params = new HttpParams()
      .set('RegId', regId.toString());
    return this.http.get<ResultWrapper<UserAccountRegDto>>(this.configHelper.getApiUrl("User/Reg/TestInfo"), { params });
  }

  getWebDeliveryTestSessions(testPurposeId: number, brandId: number, testTypeId: number, isBusinessAccount: boolean, countryAbbr: string): Observable<ResultWrapper<TestSessionsForSittingSelectionDto>> {
    const params = new HttpParams()
      .set('testPurposeId', testPurposeId.toString())
      .set('brandId', brandId.toString())
      .set('testTypeId', testTypeId.toString())
      .set('isBusinessAccount', isBusinessAccount.toString())
      .set('countryAbbr', countryAbbr);
    return this.http.get<ResultWrapper<TestSessionsForSittingSelectionDto>>(this.configHelper.getApiUrl("WebDeliveryTestSessions"), { params });
  }

  getTestSessionsForRegistration(brandId: number, testPurposeId: number, testTypeId: number, regionId: number, sourceRegId?: number): Observable<ResultWrapper<TestSessionForRegistrationDto[]>> {
    const params = new HttpParams()
      .set('testPurposeId', testPurposeId.toString())
      .set('brandId', brandId.toString())
      .set('testTypeId', testTypeId.toString())
      .set('privateOnly', 'false')
      .set('regionId', regionId.toString())
      .set('sourceRegId', sourceRegId ? sourceRegId.toString() : "");
    return this.http.get<ResultWrapper<TestSessionForRegistrationDto[]>>(this.configHelper.getApiUrl("TestSessions"), { params });
  }

  getTestSessionsByTestCentre(brandId: number, testPurposeId: number, testTypeId: number, testCentreId: number): Observable<ResultWrapper<TestSessionForRegistrationDto[]>> {
    const params = new HttpParams()
      .set('testPurposeId', testPurposeId.toString())
      .set('brandId', brandId.toString())
      .set('testTypeId', testTypeId.toString())
      .set('testCentreId', testCentreId.toString());
    return this.http.get<ResultWrapper<TestSessionForRegistrationDto[]>>(this.configHelper.getApiUrl("TestSessions"), { params });
  }

  getPrivateSittings(testPurposeId: number, brandId: number, testTypeId: number): Observable<ResultWrapper<TestSessionForRegistrationDto[]>> {
    const params = new HttpParams()
      .set('testPurposeId', testPurposeId.toString())
      .set('brandId', brandId.toString())
      .set('testTypeId', testTypeId.toString());
    return this.http.get<ResultWrapper<TestSessionForRegistrationDto[]>>(this.configHelper.getApiUrl("PrivateTestSessions"), { params });
  }

  validateTestSession(testTypeId: number, testSessionId: number): Observable<ResultWrapper<ValidateTestSessionSelectionResultDto>> {
    const params = new HttpParams()
      .set('testSessionId', testSessionId.toString())
      .set('testTypeId', testTypeId.toString());
    return this.http.get<ResultWrapper<ValidateTestSessionSelectionResultDto>>(this.configHelper.getApiUrl("User/Reg/ValidateTestSessionSelection"), { params });
  }

  addChildAccountRegToShoppingCart(childAccountReg: AddChildAccountRegToShoppingCartRequestForCael | AddChildAccountRegToShoppingCartRequestForCelpip): Observable<ResultWrapper<AddChildAccountRegToShoppingCartResponse>> {
    return this.http.post<ResultWrapper<AddChildAccountRegToShoppingCartResponse>>(this.configHelper.getApiUrl("User/Reg/ChildReg"), childAccountReg);
  }

  removeChildAccountRegFromShoppingCart(childAccountRegId: number) {
    const params = new HttpParams()
      .set('childAccountRegId', childAccountRegId.toString());
    return this.http.delete(this.configHelper.getApiUrl("User/Reg/ChildReg"), { params });
  }

  postRegPersonalInfo(saveIdentityInfoRequest: SaveUserRegIdentityInfoRequest): Observable<ResultWrapper<number>> {
    return this.http.post<ResultWrapper<number>>(this.configHelper.getApiUrl("User/Reg/IdentityInfo"), saveIdentityInfoRequest);
  }

  getIdentityInfo(): Observable<ResultWrapper<UserAccountIdentityInfoDto>> {
    return this.http.get<ResultWrapper<UserAccountIdentityInfoDto>>(this.configHelper.getApiUrl("User/Reg/IdentityInfo"));
  }

  postRegShoppingCart(regInShoppingCartRequest: RegInShoppingCartRequest): Observable<ResultWrapper<ShoppingCartDto>> {
    return this.http.post<ResultWrapper<ShoppingCartDto>>(this.configHelper.getApiUrl("User/Reg/ShoppingCart"), regInShoppingCartRequest);
  }

  getInternationalTestCentres(): Observable<ResultWrapper<TestCentreDto[]>> {
    return this.http.get<ResultWrapper<TestCentreDto[]>>(this.configHelper.getApiUrl("User/Reg/InternationalTestCentres?showHiddenTestCentre=false"));
  }

  getBiometricConsent(upcomingRegId: number): Observable<ResultWrapper<CreateAccountInitializationInfoDto>> {
    const params = new HttpParams()
      .set('upcomingRegId', upcomingRegId.toString());
    return this.http.get<ResultWrapper<CreateAccountInitializationInfoDto>>(this.configHelper.getApiUrl("User/Reg/GetBiometricConsent"), { params });
  }

  setBiometricConsent(biometricConsentRequest: BiometricConsentRequest) {
    return this.http.post(this.configHelper.getApiUrl("User/Reg/SetBiometricConsent"), biometricConsentRequest);
  }

  setTestPurposeLookUp(testTypeId: number, isWebDelivery: boolean) {
    forkJoin([
      this.getTestPurposes(testTypeId),
      this.getAdmissionInstitutions(testTypeId, isWebDelivery),
      this.getSubscribedInstitutionsList(testTypeId, isWebDelivery),
    ]).subscribe(
      result => {
        //equality compare does not work between the number type id with ===
        // eslint-disable-next-line eqeqeq
        if (testTypeId == TestTypeEnum.CELPIP_LS) {
          result[0].content = result[0].content.filter((x) => x.description !== "Other");
        }
        this.store.dispatch(setTestPurposeLookup({ testPurposes: result[0].content, institutions: result[1].content, subscribedInstitutions: result[2].content }));
      },
      error => {
        this.toastService.warning(error);
      }
    );
  }

  getTestRegistrationReceiptDataToken(userOrderId: number): Observable<string> {
    const params = new HttpParams().set('userOrderId', userOrderId.toString());
    return this.http.get<string>(this.configHelper.getApiUrl("User/Account/TestRegistrationReceiptDataToken"), { params });
  }

  getTestRegistrationReceipt(userOrderId: number, token: string, isWebDelivery: boolean) {
    return this.http.get((this.configHelper.getBaseUrl() + `/Print/TestRegistrationReceipt?userOrderId=${userOrderId}&token=${token}&isWebDelivery=${isWebDelivery}`), { responseType: 'text' });
  }

  setScoreReportPhoneNumber(request: UserAccountNotificationSettingsDto) {
    return this.http.post(this.configHelper.getApiUrl("User/Reg/SetScoreReportPhoneNumber"), request);
  }

  getScoreReportPhoneNumber(): Observable<ResultWrapper<string>> {
    return this.http.get<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Reg/GetScoreReportPhoneNumber"));
  }

  ValidateRegionalAccess(brandId: number, testSessionId: number): Observable<ResultWrapper<string>> {
    const params = new HttpParams()
      .set('brandId', brandId.toString())
      .set('testSessionId', testSessionId.toString());
    return this.http.get<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Reg/ValidateRegionalAccess"), { params });
  }

  getRegScoreFlags(regId: number): Observable<ResultWrapper<ScoreFlagDto[]>> {
    const params = new HttpParams()
      .set('RegId', regId.toString());
    return this.http.get<ResultWrapper<ScoreFlagDto[]>>(this.configHelper.getApiUrl("User/Reg/ScoreFlags"), { params });
  }

  CreateRegScoreFlag(regId: number, summary: string, additionalComments: string) {
    return this.http.post(this.configHelper.getApiUrl("User/Reg/ScoreFlag"), { regId, summary, additionalComments });
  }

  postOSRRegShoppingCart(oneSkillRetakeRegistrationRequest: OneSkillRetakeRegistrationRequest) {
    return this.http.post(this.configHelper.getApiUrl("User/Reg/OneSkillRetake"), oneSkillRetakeRegistrationRequest);
  }

}
